import type { ReactNode } from 'react';
import { Inbox } from 'lucide-react';
import { Button } from '../primitives/button';
import { PendingSlot } from './PendingSlot';
import { cn } from '../lib/cn';

export interface EmptyStateAction {
  readonly label: string;
  readonly onClick?: () => void;
  readonly href?: string;
  /**
   * Renders the action disabled with a reason (never hidden) — e.g. the
   * person's role cannot take it. It stays focusable (`aria-disabled`, not
   * native `disabled`) and activating it does nothing. Pair it with
   * `describedBy` naming the visible element that says why.
   */
  readonly disabled?: boolean;
  /** Id of the visible text explaining a disabled action. */
  readonly describedBy?: string;
  /**
   * Whether the action is available is not known yet (e.g. the role is still
   * loading): shown as a same-size placeholder, neither offered nor refused.
   */
  readonly pending?: boolean;
}

export interface EmptyStateWithNextActionProps {
  readonly title: string;
  readonly description?: string;
  readonly icon?: ReactNode;
  /** Always show exactly one clear next action (design doc §22.4). */
  readonly action?: EmptyStateAction;
  readonly className?: string;
}

export function EmptyStateWithNextAction({
  title,
  description,
  icon,
  action,
  className,
}: EmptyStateWithNextActionProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border ' +
          'bg-muted/30 px-6 py-12 text-center',
        className,
      )}
    >
      <div className="text-muted-foreground" aria-hidden="true">
        {icon ?? <Inbox className="size-8" />}
      </div>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {description ? (
        <p className="max-w-prose text-sm text-muted-foreground">{description}</p>
      ) : null}
      {action ? (
        <PendingSlot pending={action.pending === true} className="mt-2">
          <Button
            size="sm"
            className="mt-2"
            onClick={() => {
              if (!action.disabled) action.onClick?.();
            }}
            {...(action.disabled ? { 'aria-disabled': true } : {})}
            aria-describedby={action.describedBy}
            {...(action.href && !action.disabled ? { asChild: true } : {})}
          >
            {action.href && !action.disabled ? (
              <a href={action.href}>{action.label}</a>
            ) : (
              action.label
            )}
          </Button>
        </PendingSlot>
      ) : null}
    </div>
  );
}
